import { useEffect, useState } from 'react'
import api from 'helpers/api'
import { useAuth } from 'hooks/auth'
import { useHubsData } from 'providers/hubs'
import { PROJECT } from 'app-constants'

/** Shown on a disabled New hub row under a Site the user doesn't administer. */
export const HUB_CREATION_RESTRICTED_MESSAGE = `You need to be a Site Admin to create a ${PROJECT}`
/** Shown when the user can't create a hub anywhere, which may be down to an organisation they only hold a seat in. */
export const HUB_CREATION_SITE_REQUIRED_MESSAGE = `Only Site Admins can create ${PROJECT}s, because an organisation you're part of requires every ${PROJECT} to be in a Site`
/** Shown instead to an Organisation Admin, who can create a hub in any Site, while the organisation has none. */
export const HUB_CREATION_NEEDS_SITE_MESSAGE = `Create a Site first: every ${PROJECT} in your organisation must belong to a Site`

export interface HubCreationState {
  /** Sites the user can create a hub in. */
  creatableSiteIds: Set<string>
  /** Sites the user can see but not create a hub in, because their organisation restricts hub creation. */
  restrictedSiteIds: Set<string>
  /** False when the user's organisation only lets Site Admins create hubs and the user administers no site. */
  canCreateHub: boolean
  /** Why New hub is disabled when `canCreateHub` is false. */
  restrictionMessage: string
}

/** What the sites tell us; the message is worked out from the user on each render. */
type HubCreationSites = Omit<HubCreationState, 'restrictionMessage'>

const INITIAL_STATE: HubCreationSites = {
  creatableSiteIds: new Set(),
  restrictedSiteIds: new Set(),
  // Enabled until the sites arrive, so the buttons don't flash disabled for everyone; the API enforces the rule.
  canCreateHub: true
}

/** Where the current user can create hubs, from the sites they can see. */
export const useHubCreation = (): HubCreationState => {
  const { user } = useAuth()
  // Re-read with the hub list: a site created, changed or deleted is announced as a hub list change
  const { revision } = useHubsData()
  const [state, setState] = useState<HubCreationSites>(INITIAL_STATE)

  useEffect(() => {
    if (!user) return
    // A newer read (the next revision) may finish first: an older answer must not replace it
    let superseded = false
    api
      .getUserSites()
      .then((response) => {
        if (superseded) return
        const creatable = response.data.filter((site) => site.canCreateHub !== false)
        const restricted = response.data.filter((site) => site.canCreateHub === false)
        setState({
          creatableSiteIds: new Set(creatable.map((site) => site.publicId!)),
          restrictedSiteIds: new Set(restricted.map((site) => site.publicId!)),
          canCreateHub: !response.metadata?.hubCreationRestricted || creatable.length > 0
        })
      })
      // Without the list the site sections just go without New hub.
      .catch(() => {
        if (!superseded) setState(INITIAL_STATE)
      })
    return () => {
      superseded = true
    }
  }, [user?.firebaseUserId, revision])

  // An Organisation Admin administers every Site, so the only thing that can stop them is there being none
  const restrictionMessage = user?.adminOrganisations?.length
    ? HUB_CREATION_NEEDS_SITE_MESSAGE
    : HUB_CREATION_SITE_REQUIRED_MESSAGE

  return { ...state, restrictionMessage }
}
